import React, {useState} from "react";
import {pageAuthorizedByGroup} from "../fragments/PageAuthorization";
import {PentaSplit} from "../../components/common/PentaSplit";
import {
	Breadcrumbs,
	Button,
	Dialog,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Paper,
	Stack, TextField,
	Typography
} from "@mui/material";
import {Link} from "react-router-dom";
import {GraphQlDataGrid} from "../../components/common/grid/GraphQlDataGrid";
import {graphQLServices} from "../../services/GraphQLServices";
import {
	DELETE_PHRASES_ITEM_AND_INHERITED,
	EXISTS_PHRASE,
	GET_PHRASE_ITEMS,
	PUT_PHRASE_ITEM
} from "./Queries";
import {formatDateTimeShort, GlobalState} from "../../global/GlobalState";
import {textColumn, tooltipColumn} from "../../components/common/grid/Columns";
import {translationsEditorTargetStage} from "./TranslationsOverview";
import {linkPhraseItemDetailsFor} from "./PhraseItemDetails";
import {GridColDef, GridPagination} from "@mui/x-data-grid";
import {PhraseItem} from "./models/PhraseItem";
import {cloneMutationData} from "../products/Utils";
import {ProcessButton} from "../../components/common/input/ProcessButton";
import {translateFunction} from "../../localization/TranslationService";
import {translationsEditorDefaultTargetStage} from "./TranslationItemOverview";
import {GridRowSelectionModel} from "@mui/x-data-grid/models/gridRowSelectionModel";
import {GridRowId} from "@mui/x-data-grid/models/gridRows";

const translate = translateFunction("SureBasePortalTranslations");

export const PhraseItemsOverview = pageAuthorizedByGroup(function () {
	const [resetKey, setResetKey] = useState(0);
	const updateGrid = () => setResetKey(resetKey + 1);
	const stage = GlobalState.targetKiziStage(translationsEditorDefaultTargetStage);
	const [rowSelectionModel, setRowSelectionModel] =
		React.useState<GridRowSelectionModel>([]);
	
	function onSelectionModelChange(selectionModel: GridRowSelectionModel) {
		setRowSelectionModel(selectionModel);
	}
	
	const columns: GridColDef[] = [
		{
			field: "",
			headerName: translate("Open"),
			renderCell: (params) => (
				<Link to={linkPhraseItemDetailsFor(params.row.id)} style={{
					textDecoration: "initial"
				}}>
					<Button size="small" tabIndex={-1}>{translate("Open")}</Button>
				</Link>
			),
			sortable: false,
			editable: false,
			disableColumnMenu: false,
			filterable: false,
			hideable: false,
		},
		{
			field: "phrase",
			headerName: translate("Phrase"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "creationDateTime",
			headerName: translate("Creation Date/Time"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			valueFormatter: (params) => formatDateTimeShort(params.value),
			flex: 1,
		},
		{
			field: "lastModifiedBy",
			headerName: translate("Last Modified By"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "lastModifiedDateTime",
			headerName: translate("Last Modified Date/Time"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			valueFormatter: (params) => formatDateTimeShort(params.value),
			flex: 1
		}
	];
	
	return <PentaSplit
		top={<Breadcrumbs style={{margin: "5px"}}>
			<Link style={{color: "inherit", textDecoration: "unset"}}
				to="/translations_setup">{translate("Translation Setup")}</Link>
			<Typography color="text.primary">{translate("Phrases")}</Typography>
		</Breadcrumbs>}
		center={<Paper style={{
			height: "100%",
		}}>
			<GraphQlDataGrid
				enableHeaderFilter
				checkboxSelection={stage === "kizidev"}
				onRowSelectionModelChange={onSelectionModelChange}
				rows={[]}
				query={GET_PHRASE_ITEMS}
				graphFunction={"phraseItems"}
				client={graphQLServices.translationsClient}
				rowsPerPageOptions={[50, 500, 1000]}
				initialPageSize={50}
				columns={columns}
				getRowId={row => row.id}
				defaultSortModel={[
					{
						field: "phrase",
						sort: "asc"
					}
				]}
				queryVariables={{stage: translationsEditorTargetStage, resetKey}}
				initialState={{
					columns: {
						columnVisibilityModel: {
							creationDateTime: false
						},
					},
				}}
				slots={{footer: CustomFooterFactory(stage, rowSelectionModel, updateGrid)}}
			/>
		</Paper>}
	/>;
}, ["Admin", "Translations"]);

async function phraseExists(item: PhraseItem): Promise<boolean> {
	
	const existsItems = await graphQLServices.translationsClient.query(
		{
			query: EXISTS_PHRASE,
			variables: {
				stage: translationsEditorTargetStage,
				phrase: item.phrase,
			},
			fetchPolicy: "no-cache"
		}
	);
	
	const nodes: any[] = existsItems.data.phraseItems.nodes;
	return nodes.length > 0;
}

async function saveChanges(phraseItem: PhraseItem) {
	await graphQLServices.translationsClient.mutate({
		mutation: PUT_PHRASE_ITEM,
		variables: {
			stage: translationsEditorTargetStage,
			phrase: cloneMutationData(phraseItem),
		}
	});
}

function CustomFooterFactory(stage: string, selectedItems: GridRowId[], updateGrid: () => void) {
	return function CustomFooter(props: any) {
		const [open, setOpen] = useState(false);
		
		const [phrase, setPhrase] = useState("");
		const [openDelete, setOpenDelete] = useState(false);
		const [deleteConfirmation, setDeleteConfirmation] = useState(false);
		const [deleteCount, setDeleteCount] = useState(0);
		
		function handleClickOpen() {
			return setOpen(true);
		}
		
		function handleClose() {
			return setOpen(false);
		}
		
		async function handleCreate() {
			
			const id = crypto.randomUUID();
			const newItem = {id, phrase} as PhraseItem;
			await phraseExists(newItem).then(async (x) => {
				if (x) {
					throw new Error(`Item already exists`);
				}
				
				await saveChanges(newItem);
				setOpen(false);
				updateGrid();
			});
		}
		
		function handleDeleteOpen() {
			setOpenDelete(true);
		}
		
		function handleDeleteClose() {
			if (deleteCount > 0)
				updateGrid();
			setOpenDelete(false);
			setDeleteCount(0);
			setDeleteConfirmation(false);
		}
		
		async function handleDelete() {
			const items = selectedItems.map((x) => x.toString());
			const result = await graphQLServices.translationsClient.mutate({
				mutation: DELETE_PHRASES_ITEM_AND_INHERITED,
				variables: {
					phraseIds: items,
					stage
				}
			});
			
			setDeleteCount(result.data.deletePhrasesItemAndInherited.deleteCount);
			setDeleteConfirmation(true);
		}
		
		
		return <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"} style={{
			padding: "0 10px"
		}}>
			<Stack direction={"row"} spacing={2} alignItems={"center"}>
				<Button
					variant={"contained"}
					onClick={handleClickOpen}
				>{translate("Add")}</Button>
				{selectedItems.length > 0 &&
					<Button
						key={"deletePhrasesBtn"}
						variant={"contained"}
						onClick={handleDeleteOpen}
						disabled={stage !== translationsEditorTargetStage}
					>{translate("Delete Phrases")}</Button>}
			</Stack>
			<Dialog open={openDelete} onClose={handleDeleteClose}>
				<DialogTitle>{translate("Delete Phrases")}</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{!deleteConfirmation && translate("Delete {numberOfItems} phrases and any translation items using them?").replace("{numberOfItems}", selectedItems.length.toString())}
						
						{deleteConfirmation && deleteCount > 0 && translate("Successfully deleted {deleteCount} phrases.").replace("{deleteCount}", deleteCount.toString())}
						
						{deleteConfirmation && deleteCount === 0 && translate("No phrases deleted")}
					
					</DialogContentText>
					<Stack direction={"row"} justifyContent={"space-between"} style={{marginTop: "0px"}}>
						{deleteConfirmation && <Button onClick={handleDeleteClose}>{translate("Close")}</Button>}
						{!deleteConfirmation && <Button onClick={handleDeleteClose}>{translate("Cancel")}</Button>}
						{!deleteConfirmation &&
							<Button onClick={() => handleDelete()} color={"success"}>{translate("Delete")}</Button>}
					</Stack>
				</DialogContent>
			</Dialog>
			<Dialog open={open} onClose={handleClose}>
				<DialogTitle>{translate("New Phrase")}</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{translate("Please provide the following information.")}
					</DialogContentText>
					<form>
						<TextField
							margin="dense"
							label={translate("Phrase")}
							fullWidth
							variant="standard"
							required
							value={phrase}
							onChange={event => {
								setPhrase(event.target.value);
							}}
						/>
						<Stack direction={"row"} justifyContent={"space-between"} style={{marginTop: "0px"}}>
							<Button onClick={handleClose}>{translate("Cancel")}</Button>
							<div style={{flexGrow: 0, padding: "10px"}}>
								<ProcessButton
									defaultText={translate("Create")}
									inProgressText={translate("Creating")}
									completedText={translate("Created")}
									resetKeys={[phrase]}
									variant={"outlined"}
									onClick={handleCreate}/>
							</div>
						</Stack>
					</form>
				</DialogContent>
			</Dialog>
			<GridPagination {...props} />
		</Stack>;
	};
}
